import DOMPurify from "dompurify";

const EMAIL_SEPARATOR = "^^^EMAIL SEPARATOR^^^";

const ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "b",
  "i",
  "ul",
  "ol",
  "li",
  "a",
  "span",
  "div",
];
const ALLOWED_ATTR = ["href", "dir", "class", "style"];

const stripMarkdownFences = (text: string): string => {
  const trimmed = text.trim();
  const fenced = trimmed.match(/^```(?:html)?\s*\n?([\s\S]*?)\n?```\s*$/i);
  if (fenced) {
    return fenced[1].trim();
  }
  if (trimmed.startsWith("```")) {
    const lines = trimmed.split("\n");
    const body = lines.slice(1, lines[lines.length - 1]?.trim() === "```" ? -1 : undefined);
    return body.join("\n").trim();
  }
  return trimmed;
};

const extractLegacySeparatorBody = (text: string): string => {
  const parts = text.split(EMAIL_SEPARATOR);
  if (parts.length === 3) {
    return (parts[1] || "").trim();
  }
  return text;
};

export const stripMetaPreamble = (html: string): string => {
  if (!html?.trim()) return html;
  let s = html;

  s = s.replace(
    /<p[^>]*>\s*here\s+is\s+the\s+(modified|updated|revised|new|rewritten)?\s*email\s*(body|text)?\s*:?\s*<\/p>/gi,
    "",
  );

  const pMatch = s.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
  if (pMatch) {
    const fullMatch = pMatch[0];
    const inner = pMatch[1];
    const strippedInner = inner.replace(
      /^\s*here\s+is\s+the\s+(modified|updated|revised|new|rewritten)?\s*email\s*(body|text)?\s*:\s*/i,
      "",
    );
    if (strippedInner !== inner) {
      const before = s.slice(0, pMatch.index ?? 0);
      const after = s.slice((pMatch.index ?? 0) + fullMatch.length);
      const openTagEnd = fullMatch.indexOf(">") + 1;
      const openTag = fullMatch.slice(0, openTagEnd);
      s = `${before}${openTag}${strippedInner}</p>${after}`;
    }
  }

  s = s.replace(
    /^\s*here\s+is\s+the\s+(modified|updated|revised|new|rewritten)?\s*email\s*(body|text)?\s*:\s*\n*/i,
    "",
  );
  s = s.replace(
    /^\s*(modified|updated|revised|rewritten)\s+email\s*(body|text)?\s*:\s*\n*/i,
    "",
  );

  return s.replace(/^\s+/, "");
};

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const plainTextToHtml = (text: string): string => {
  const paragraphs = text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (paragraphs.length === 0) {
    const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
    return lines.map((line) => `<p>${escapeHtml(line)}</p>`).join("");
  }
  return paragraphs
    .map((para) => {
      const inner = para
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
        .map(escapeHtml)
        .join("<br>");
      return `<p>${inner}</p>`;
    })
    .join("");
};

const collapseInterTagWhitespace = (html: string): string =>
  html.replace(/>\s*\n\s*</g, "><").replace(/>\s+</g, "><").trim();

/** Placeholder so existing <br>/newlines are not treated as tag boundaries. */
const BR_TOKEN = "\uE000";

const protectExistingBreaks = (html: string): string =>
  html.replace(/<br\s*\/?>/gi, BR_TOKEN).replace(/\r?\n/g, BR_TOKEN);

const restoreProtectedBreaks = (html: string): string =>
  html.replaceAll(BR_TOKEN, "<br>");

const hasEarlierSentenceInBlock = (text: string, periodIndex: number): boolean =>
  /\.\s*\S/.test(text.slice(0, periodIndex));

/**
 * Insert <br> after sentence-ending periods inside block elements.
 * Skips decimals, existing breaks (LLM <br>/newlines), and duplicate breaks.
 */
export const insertLineBreaksAfterPeriods = (html: string): string => {
  if (!html.includes(".")) return html;

  const protectedHtml = protectExistingBreaks(html);

  const withBreaks = protectedHtml.replace(
    /(<(?:p|div|li|h[1-6])[^>]*>)([\s\S]*?)(<\/(?:p|div|li|h[1-6])>)/gi,
    (_match, open: string, inner: string, close: string) => {
      if (!inner.includes(".")) return `${open}${inner}${close}`;

      const updated = inner.replace(
        /\.(?!\d)/g,
        (period, periodIndex, text) => {
          const after = text.slice(periodIndex + period.length);
          if (/^\s*(?:<br\s*\/?>|<\/(?:p|div|li|h[1-6])\b)/i.test(after)) {
            return period;
          }
          if (after.startsWith(BR_TOKEN) || /^\s*\r?\n/.test(after)) {
            return period;
          }
          if (/^\s*$/.test(after)) {
            if (!hasEarlierSentenceInBlock(text, periodIndex)) return period;
            // Trailing period: skip if LLM already used line breaks in this block.
            if (text.includes(BR_TOKEN)) return period;
            return ".<br>";
          }
          return ".<br>";
        },
      );

      return `${open}${updated}${close}`;
    },
  );

  return restoreProtectedBreaks(withBreaks);
};

/**
 * Normalize LLM or stored draft body HTML for TipTap.
 */
export const parseDraftHtml = (raw: string | null | undefined): string => {
  if (!raw?.trim()) return "";

  let text = stripMarkdownFences(raw);
  text = extractLegacySeparatorBody(text);
  text = stripMetaPreamble(text);

  if (!text.includes("<")) {
    text = plainTextToHtml(text);
  }

  text = collapseInterTagWhitespace(text);
  text = insertLineBreaksAfterPeriods(text);

  if (typeof window !== "undefined") {
    return DOMPurify.sanitize(text, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
    });
  }

  // Node/test fallback when jsdom is unavailable
  return text.replace(/<script[\s\S]*?<\/script>/gi, "");
};
